import React, { useEffect, useState } from 'react';
import ConstructionAPI from './api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Search, Package, HardHat, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function RateSheetView({ region: regionProp }) {
  const [region, setRegion] = useState(regionProp || 'us');
  const [regions, setRegions] = useState([]);
  const [tab, setTab] = useState('materials');
  const [materials, setMaterials] = useState([]);
  const [labour, setLabour] = useState([]);
  const [search, setSearch] = useState('');
  const [mult, setMult] = useState(1);
  const [location, setLocation] = useState('');
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveOverlay, setLiveOverlay] = useState({}); // { item_code: { usd_rate, source, confidence, fetched_at } }
  const [liveSummary, setLiveSummary] = useState(null);

  useEffect(() => {
    ConstructionAPI.getRegions().then(r => {
      setRegions(r.regions || []);
      // Pre-fill location with the region label for LLM lookup
      const found = (r.regions || []).find(rr => rr.key === region);
      if (found && !location) setLocation(found.label);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (regionProp && regionProp !== region) setRegion(regionProp);
    // eslint-disable-next-line
  }, [regionProp]);

  useEffect(() => {
    ConstructionAPI.getMaterials(region).then(d => { setMaterials(d.items || []); setMult(d.cost_multiplier); });
    ConstructionAPI.getLabour(region).then(d => setLabour(d.items || []));
    setLiveOverlay({});
    setLiveSummary(null);
    const found = regions.find(rr => rr.key === region);
    if (found) setLocation(found.label);
  }, [region]); // eslint-disable-line

  const list = tab === 'materials' ? materials : labour;
  const filtered = list.filter(r =>
    !search ||
    r.description.toLowerCase().includes(search.toLowerCase()) ||
    r.item_code.toLowerCase().includes(search.toLowerCase()) ||
    (r.category || '').toLowerCase().includes(search.toLowerCase())
  );

  const refreshLive = async () => {
    if (!location.trim()) {
      toast.error('Enter a location (e.g. "Dubai, UAE") to fetch live market rates');
      return;
    }
    const items = filtered.map(r => ({ item_code: r.item_code, description: r.description, unit: r.unit }));
    if (items.length === 0) { toast.error('No items in current view to refresh'); return; }
    try {
      setLiveLoading(true);
      const res = await ConstructionAPI.liveRefreshRates(items, location.trim());
      if (res?.ok === false) {
        toast.error(res.error || 'Live rate fetch failed');
        return;
      }
      const overlay = {};
      (res.items || []).forEach(it => { if (it.item_code) overlay[it.item_code] = it; });
      setLiveOverlay(overlay);
      setLiveSummary({
        location: res.location || location.trim(),
        fetched_at: res.refreshed_at,
        source: res.model || 'AI',
        notes: res.disclaimer,
        count: (res.items || []).filter(it => it.usd_rate != null).length,
      });
      toast.success(`Fetched live rates for ${location.trim()}`);
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail || 'Live rate fetch failed';
      toast.error(detail);
    } finally {
      setLiveLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="rate-sheet-view">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-900 rounded-lg"><Package className="h-5 w-5 text-white" /></div>
              <div>
                <CardTitle className="text-base">Construction Rate Sheets</CardTitle>
                <p className="text-xs text-slate-500">USD base rates with regional cost multiplier applied.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                <Input className="pl-8 h-9 w-56" placeholder="Search items…" value={search} onChange={e => setSearch(e.target.value)} data-testid="rate-search" />
              </div>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger className="h-9 w-40"><SelectValue /></SelectTrigger>
                <SelectContent>{regions.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <button onClick={() => setTab('materials')} className={`px-3 py-1.5 text-sm rounded-full border ${tab === 'materials' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300'}`} data-testid="rate-tab-materials"><Package className="h-3.5 w-3.5 inline mr-1" />Materials ({materials.length})</button>
            <button onClick={() => setTab('labour')} className={`px-3 py-1.5 text-sm rounded-full border ${tab === 'labour' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300'}`} data-testid="rate-tab-labour"><HardHat className="h-3.5 w-3.5 inline mr-1" />Labour ({labour.length})</button>
            <span className="text-xs text-slate-500 ml-auto">Region multiplier × {mult}</span>
          </div>

          {/* Live rate refresh row */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-3 mb-3 flex items-center gap-2 flex-wrap" data-testid="live-rates-bar">
            <div className="flex items-center gap-2 min-w-0">
              <div className="p-1.5 bg-indigo-600 rounded-md"><Sparkles className="h-3.5 w-3.5 text-white" /></div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">Live market rates (AI)</div>
                <div className="text-[11px] text-slate-600 truncate">Fetches up-to-date rates from public sources for your location.</div>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-auto flex-wrap">
              <Input
                className="h-9 w-56"
                placeholder='Location e.g. "Dubai, UAE"'
                value={location}
                onChange={e => setLocation(e.target.value)}
                data-testid="live-rates-location"
              />
              <Button onClick={refreshLive} disabled={liveLoading} className="h-9 bg-indigo-600 hover:bg-indigo-700" data-testid="live-rates-refresh-btn">
                {liveLoading ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Fetching…</> : <><Sparkles className="h-3.5 w-3.5 mr-1.5" />Refresh live rates</>}
              </Button>
            </div>
            {liveSummary && (
              <div className="basis-full text-xs text-indigo-900/80 mt-1" data-testid="live-rates-summary">
                {liveSummary.count} item(s) refreshed for <strong>{liveSummary.location}</strong> · model: {liveSummary.source}
                {liveSummary.notes ? ` · ${liveSummary.notes}` : ''}
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2.5">Code</th>
                  <th className="text-left p-2.5">Description</th>
                  <th className="text-left p-2.5">Category</th>
                  <th className="text-left p-2.5">Unit</th>
                  <th className="text-right p-2.5">USD base</th>
                  <th className="text-right p-2.5">Regional</th>
                  <th className="text-right p-2.5">Live</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const live = liveOverlay[r.item_code];
                  const liveRate = live?.usd_rate;
                  const delta = liveRate != null ? ((liveRate - r.usd_rate) / r.usd_rate) * 100 : null;
                  return (
                    <tr key={r.item_code} className="border-t hover:bg-slate-50">
                      <td className="p-2.5 font-mono text-xs">{r.item_code}</td>
                      <td className="p-2.5">{r.description}</td>
                      <td className="p-2.5"><Badge variant="outline" className="capitalize text-xs">{r.category.replace('_', ' ')}</Badge></td>
                      <td className="p-2.5">{r.unit}</td>
                      <td className="text-right p-2.5">${r.usd_rate.toLocaleString()}</td>
                      <td className="text-right p-2.5 font-medium">${r.regional_rate.toLocaleString()}</td>
                      <td className="text-right p-2.5">
                        {liveRate != null ? (
                          <div className="inline-flex flex-col items-end">
                            <span className="font-semibold text-indigo-700">${Number(liveRate).toLocaleString()}</span>
                            {delta != null && (
                              <span className={`text-[10px] ${delta >= 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
                              </span>
                            )}
                          </div>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No items match your search.</div>}
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Upload your own rate sheet per project inside the calculator (coming soon). CSV format:
            <code className="ml-1 bg-slate-100 px-1.5 py-0.5 rounded">item_code,description,category,unit,usd_rate</code>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
